import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { BarChart3, Download, Mail, PlusIcon, Zap } from 'lucide-react'

import { Button, Card, CardContent } from '@/components/ui'
import { useAccount } from '@/modules/account'
import { useFolders } from '@/modules/folders'
import {
  CreateProjectDialog,
  useFetchAllProjects,
  useLoadingProjects,
  useProjects,
} from '@/modules/projects'
import { DashboardSkeleton, ProjectUsageDashboard } from '@/modules/usage'
import { useDialogActions } from '@/stores/dialog'

export const Route = createFileRoute('/_authenticated/')({
  staticData: { title: 'Dashboard' },
  component: Dashboard,
})

function Dashboard() {
  useFetchAllProjects()
  const projects = useProjects()
  const folders = useFolders()
  const loadingProjects = useLoadingProjects()
  const account = useAccount()

  if (loadingProjects) {
    return <DashboardSkeleton />
  }

  const hasNoProjects = projects.length === 0 && folders.length === 0

  if (hasNoProjects) {
    return <DashboardEmptyState firmName={account?.name} />
  }

  const firmName = account?.name

  return (
    <div className="space-y-6">
      <div className="space-y-1.5">
        <h1 className="text-3xl font-bold tracking-tight">
          {firmName ? `${firmName} Dashboard` : 'Dashboard'}
        </h1>
        <p className="text-muted-foreground text-lg">
          Billable analysis across your cases, ready to invoice.
        </p>
      </div>

      <ProjectUsageDashboard projects={projects} account={account} />
    </div>
  )
}

const gettingStartedSteps = [
  {
    title: 'Create a Project',
    description: 'Name your project and optionally assign it to a folder',
  },
  {
    title: 'Upload Statements',
    description: 'Upload PDF bank or credit card statements to your project',
  },
  {
    title: 'Review Results',
    description:
      "We'll process your documents and extract transactions automatically",
  },
]

const valuePropositions = [
  {
    icon: BarChart3,
    title: 'Track Progress',
    description:
      'Monitor projects, pages processed, and time saved across your firm',
    color: { bg: 'bg-blue-50', text: 'text-blue-600' },
  },
  {
    icon: Zap,
    title: 'Save Time',
    description:
      'Reduce manual review time with automated statement processing',
    color: { bg: 'bg-amber-50', text: 'text-amber-600' },
  },
  {
    icon: Download,
    title: 'Export Reports',
    description: 'Generate CSV and PDF reports for billing and compliance',
    color: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
  },
]

function DashboardEmptyState({ firmName }: { firmName?: string }) {
  const navigate = useNavigate()
  const { openDialog } = useDialogActions()

  const handleCreateProject = () => {
    navigate({ to: '/projects' }).then(() => {
      openDialog(CreateProjectDialog.id)
    })
  }

  return (
    <div className="space-y-10">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">
          {firmName ? `Welcome, ${firmName}!` : 'Welcome!'}
        </h1>
        <p className="text-muted-foreground text-pretty text-lg">
          Get started by creating your first project and uploading statements
          for analysis
        </p>
      </div>

      <Card>
        <CardContent className="space-y-8 p-8">
          <div className="grid gap-6 sm:grid-cols-3">
            {gettingStartedSteps.map((step, index) => (
              <div
                key={step.title}
                className="flex flex-col items-center gap-3 text-center"
              >
                <div className="bg-brand/10 flex size-10 items-center justify-center rounded-full">
                  <span className="text-brand text-sm font-semibold">
                    {index + 1}
                  </span>
                </div>
                <h3 className="font-semibold">{step.title}</h3>
                <p className="text-muted-foreground text-pretty text-sm">
                  {step.description}
                </p>
              </div>
            ))}
          </div>

          <div className="flex justify-center">
            <div className="relative flex items-center justify-center">
              <span className="bg-brand/20 absolute size-12 animate-ping rounded-full" />
              <Button className="relative" onClick={handleCreateProject}>
                <PlusIcon className="size-4" />
                Create your first project
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 sm:grid-cols-3">
        {valuePropositions.map((item) => (
          <Card key={item.title}>
            <CardContent className="flex flex-col items-center gap-3 p-6 text-center">
              <div
                className={`${item.color.bg} flex size-10 items-center justify-center rounded-full`}
              >
                <item.icon className={`${item.color.text} size-5`} />
              </div>
              <h3 className="font-semibold">{item.title}</h3>
              <p className="text-muted-foreground text-pretty text-sm">
                {item.description}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-start gap-3 rounded-lg border px-5 py-4">
        <Mail className="text-muted-foreground mt-0.5 size-5 shrink-0" />
        <p className="text-muted-foreground text-pretty text-sm">
          Need help? You can always email us at{' '}
          <a
            href="mailto:support@counselpro.ai"
            className="text-foreground font-medium underline underline-offset-4"
          >
            support@counselpro.ai
          </a>{' '}
          or{' '}
          <a
            href="https://counselpro.ai/contact"
            className="text-foreground font-medium underline underline-offset-4"
          >
            schedule some time to talk with us
          </a>{' '}
          for a first-time walkthrough
        </p>
      </div>
    </div>
  )
}
